import axios from 'axios';
import api from '@src/api';
import cloneDeep from 'lodash/cloneDeep';
import store from '@state/store';

export const state = initialState();

function initialState() {
  return {
    currentUser: getSavedState('auth.currentUser'),
    totalUnread: 0,
    latestMessages: {},
    chatData: {},
    authChecked: false,
  };
}

export const getters = {
  loggedIn(state) {
    return !!state.currentUser;
  },
  isSubscribed(state) {
    return state.currentUser.trainer.profile.subscribed
      ? !!state.currentUser.trainer.profile.subscribed
      : false;
  },
  roles(state) {
    return (
      state.currentUser &&
      Object.keys(state.currentUser.roles).filter((key) => {
        return state.currentUser.roles[key];
      })
    );
  },
  products(state) {
    return state.currentUser.products;
  },
  userName(state) {
    if (state.currentUser && state.currentUser.roles) {
      if (state.currentUser.roles.isAdmin) {
        return state.currentUser.admin.user;
      } else if (state.currentUser.roles.isTrainer) {
        return state.currentUser.trainer.user;
      }
    }
  },
  userOrgId(state) {
    if (state.currentUser && state.currentUser.roles) {
      if (state.currentUser.roles.isAdmin) {
        return state.currentUser.admin.org_id;
      } else if (state.currentUser.roles.isTrainer) {
        return state.currentUser.trainer.org_id;
      }
    }
  },
  userOrgName(state) {
    if (state.currentUser) {
      if (state.currentUser.org) {
        return state.currentUser.org.name;
      }
    }
  },
  trainerId(state) {
    if (state.currentUser && state.currentUser.roles) {
      if (state.currentUser.roles.isTrainer) {
        return state.currentUser.trainer._id;
      }
    }
  },
  trainerFullName(state) {
    if (state.currentUser && state.currentUser.roles) {
      if (state.currentUser.roles.isTrainer) {
        return `${state.currentUser.trainer.firstName} ${state.currentUser.trainer.lastName}`;
      }
    }
  },
  trainerFirstName(state) {
    if (state.currentUser && state.currentUser.roles) {
      if (state.currentUser.roles.isTrainer) {
        return `${state.currentUser.trainer.firstName}`;
      }
    }
  },
  trainerEmail(state) {
    if (state.currentUser && state.currentUser.roles) {
      if (state.currentUser.roles.isTrainer) {
        return state.currentUser.trainer.email;
      }
    }
  },
  streamAppId(state) {
    if (state.currentUser && state.currentUser.streamAppId) {
      return state.currentUser.streamAppId;
    }
  },
  fitnessToken(state) {
    if (state.currentUser) {
      if (state.currentUser.roles.isAdmin) {
        return state.currentUser.adminToken;
      } else if (state.currentUser.roles.isTrainer) {
        return state.currentUser.trainerToken;
      }
    }
  },
  latestMessages(state) {
    return state.latestMessages;
  },
  isTrainerEnrolledInACohort(state) {
    if (state.currentUser && state.currentUser.roles) {
      if (state.currentUser.roles.isTrainer) {
        if (
          state.currentUser.trainer &&
          state.currentUser.trainer.cohorts &&
          state.currentUser.trainer.cohorts.length > 0
        ) {
          // trainer is enrolled
          return true;
        }
        return false;
      }
      return false;
    }
    return false;
  },
  campaigns(state) {
    return state.currentUser?.org?.growCampaigns;
  },
};

export const mutations = {
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = newValue;
    saveState('auth.currentUser', newValue);
    setDefaultAuthHeaders(state);
  },
  SET_CURRENT_USER_FITNESS_TOKEN(state, newValue) {
    if (newValue.admin) {
      state.currentUser.admin = {
        org_id: state.currentUser.admin.org_id,
        ...newValue.admin,
      };
      state.currentUser.adminToken =
        newValue.adminToken || newValue.updatedToken;
    }
    if (newValue.trainer) {
      state.currentUser.trainer = {
        org_id: state.currentUser.trainer.org_id,
        ...newValue.trainer,
      };
      state.currentUser.trainerToken =
        newValue.trainerToken || newValue.updatedToken;
    }
    saveState('auth.currentUser', state.currentUser);
    setDefaultAuthHeaders(state);
  },
  SET_ACTIVE_PRODUCTS(state, newValue) {
    if (newValue.trainer) {
      state.currentUser.products = newValue.products;
    }
    saveState('auth.currentUser', state.currentUser);
  },
  SET_STREAM_APP_ID(state, newValue) {
    if (state.currentUser) {
      state.currentUser.streamAppId = newValue;
    }
    saveState('auth.currentUser', state.currentUser);
  },
  SET_UNREAD_COUNT(state, newValue) {
    if (state.currentUser) {
      state.totalUnread = newValue;
    }
  },
  SET_LATEST_MESSAGES(state, messageArray) {
    if (state.currentUser) {
      state.latestMessages = messageArray;
    }
  },
  SET_CHAT_DATA(state, newValue) {
    if (state.currentUser) {
      state.chatData = newValue;
    }
  },
  SET_UPDATE_CHAT_DATA(state, newValue) {
    if (state.currentUser) {
      const i = state.chatData.findIndex(
        (chat) => chat.clientId === newValue.clientId
      );
      const chatDataClone = cloneDeep(state.chatData);
      if (i >= 0) {
        chatDataClone[i] = newValue;
      } else {
        chatDataClone.push(newValue);
      }
      state.chatData = chatDataClone;
    }
  },
  SET_CURRENT_USER_ORG(state, value) {
    if (state.currentUser) {
      const currentUser = cloneDeep(state.currentUser);
      currentUser.org = value;
      state.currentUser = currentUser;
    }
  },
  REMOVE_PRICE_FROM_PRODUCT(state, priceId) {
    const trainer = state.currentUser.trainer;
    const newPricing = trainer.myProducts[0].plans.filter(
      (price) => price.id !== priceId
    );
    state.currentUser.trainer.myProducts[0].plans = newPricing;
  },
  ADD_PRICE_TO_PRODUCT(state, priceObj) {
    const trainer = state.currentUser.trainer;
    const newPricing = [...trainer.myProducts[0].plans, priceObj];
    state.currentUser.trainer.myProducts[0].plans = newPricing;
  },
  ADD_COUPON(state, coupon) {
    const trainer = state.currentUser.trainer;
    const newCoupons = [...trainer.myCoupons, coupon];
    state.currentUser.trainer.myCoupons = newCoupons;
  },
  REMOVE_COUPON(state, couponId) {
    const trainer = state.currentUser.trainer;
    const newCoupons = trainer.myCoupons.filter(
      (coupon) => coupon.id !== couponId
    );
    state.currentUser.trainer.myCoupons = newCoupons;
  },
  SET_AUTH_CHECKED(state, value) {
    state.authChecked = value;
  },
  reset(state) {
    // acquire initial state
    const s = initialState();
    Object.keys(s).forEach((key) => {
      state[key] = s[key];
    });
  },
};

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    setDefaultAuthHeaders(state);
  },
  async hydrate({ state, getters, dispatch, commit }) {
    // Get current user's org data if it doesn't exist
    if (!state?.currentUser?.org) {
      await dispatch('getOrg', getters.userOrgId);
    }

    // // // // // // // // // // // // // // // // // // // //
    // HERE ADD ANYTHING YOU WANT TO FIRE ON ROUTER CHANGE   //
    // // // // // // // // // // // // // // // // // // // //
    if(getters.userOrgId !== '618183d65e84466f63c38878') {
      store.dispatch('prospect/getAllProspects'); // Obtain updated unread count during navigation
    }
  },
  // Logs in the current user.
  logIn({ commit, dispatch, getters }, { username, password, org } = {}) {
    if (getters.loggedIn) {
      commit('SET_AUTH_CHECKED', false);
      dispatch('validate');
    }

    return axios
      .post(api.URL_LOGIN, { email: username, password, org_id: org })
      .then((response) => {
        if (response.data.success) {
          const user = response.data;
          if (window.userpilot && user.roles.isTrainer) {
            window.userpilot.identify(
              user.trainer._id, // Used to identify users
              {
                name: user.trainer.name, // Full name
                email: user.trainer.email, // Email address
                role: user.roles.isTrainer
                  ? 'trainer'
                  : user.roles.isAdmin
                  ? 'admin'
                  : 'user',
              }
            );
          }
          commit('SET_CURRENT_USER', user);
          store.dispatch('nutrition/init');
          store.dispatch('recipe/init');
          return user;
        } else {
          throw response.data.error;
        }
      })
      .catch((err) => {
        console.warn(err);
        throw err;
      });
  },

  getOrg({ commit }, orgID) {
    const url = api.URL_GET_ORG.replace(':orgID', orgID);
    // Minimum data check for obj
    if (orgID !== undefined) {
      return axios
        .get(url)
        .then((res) => {
          if (res.data.success) {
            const streamAppId = res.data.org.stream.appId;
            commit('SET_STREAM_APP_ID', streamAppId);
            commit('SET_CURRENT_USER_ORG', res.data.org);
          }
        })
        .catch((err) => {
          console.warn(err);
        });
    } else {
      console.error(
        'orgID must contain the following properties to be a valid replacement [ orgID ]',
        orgID
      );
    }
  },

  // Logs out the current user.
  async logOut({ commit, getters }) {
    // Disconnect chat client and remove event listener
    const client = await getters.streamChatClient;
    if (client) {
      await client.off(countUnread);
      await client.disconnect();
    }

    // Continue and remove current user and require auth check after login again
    commit('SET_AUTH_CHECKED', false);
    commit('SET_CURRENT_USER', null);
  },

  // Registers a new trainer
  register({ commit, dispatch, getters }, data = {}) {
    const url = api.URL_CREATE_TRAINER_SELF.replace(
      ':orgID',
      process.env.VUE_APP_ORG_ID
    );

    commit('SET_AUTH_CHECKED', false);

    return axios
      .post(url, data)
      .then((response) => {
        if (response && response.status && response.status === 200) {
          const user = response.data;
          if (window.userpilot) {
            window.userpilot.identify(
              user.trainer._id, // Used to identify users
              {
                name: user.trainer.name, // Full name
                email: user.trainer.email, // Email address
                created_at: Date.now(),
                role: user.roles.isTrainer
                  ? 'trainer'
                  : user.roles.isAdmin
                  ? 'admin'
                  : 'user',
              }
            );
          }
          commit('SET_CURRENT_USER', user);
          commit('SET_CURRENT_USER_FITNESS_TOKEN', user);
          store.dispatch('nutrition/init');
          store.dispatch('recipe/init');
        }
        return response;
      })
      .catch((err) => {
        console.warn(err);
        if (err && err.response && err.response) {
          return err.response;
        }
      });
  },

  checkout({ commit, dispatch, getters }, data = {}) {
    const url = api.URL_TRAINER_CHECKOUT.replace(':trainerID', data.trainer_id);
    commit('SET_AUTH_CHECKED', false);
    return axios
      .post(url, data)
      .then((response) => {
        return response;
      })
      .catch((err) => {
        return err.response;
      });
  },

  checkCoupon({ commit, dispatch, getters }, coupon) {
    const url = api.URL_TRAINER_CHECK_COUPON.replace(':coupon', coupon);
    return axios
      .get(url)
      .then((response) => {
        return response;
      })
      .catch((err) => {
        return err;
      });
  },
  changePassword(ctx, data = {}) {
    const url = api.URL_PASSWORD_CHANGE.replace(':modelType', data.resetType);
    const postParams = {
      password: data.password,
      trainer_id: data.trainer_id,
    };

    return axios
      .post(url, postParams)
      .then((response) => {
        const { data: passwordChangeResponse } = response;
        return passwordChangeResponse;
      })
      .catch((err) => {
        console.warn(err);
      });
  },
  resetPassword({ commit, dispatch, getters }, data = {}) {
    const url = api.URL_PASSWORD_RESET.replace(':modelType', data.resetType);
    commit('SET_AUTH_CHECKED', false);
    const postParams = {
      email: data.email,
      org_id: process.env.VUE_APP_ORG_ID,
    };

    return axios
      .post(url, postParams)
      .then((response) => {
        const passwordResetResponse = response.data;
        return passwordResetResponse;
      })
      .catch((err) => {
        console.warn(err);
      });
  },
  confirmPasswordReset(
    { commit, dispatch, getters },
    { resetType, token, newPass }
  ) {
    const url = api.URL_PASSWORD_RESET_CONFIRM.replace(':modelType', resetType);
    commit('SET_AUTH_CHECKED', false);
    const data = {
      password: newPass,
    };

    return axios
      .post(url, data, { headers: { token: token } })
      .then((response) => {
        const passwordResetConfirmResponse = response.data;
        return passwordResetConfirmResponse;
      })
      .catch((err) => {
        console.warn(err);
      });
  },
  addPrice({ commit, dispatch, getters }, priceObj) {
    commit('ADD_PRICE_TO_PRODUCT', priceObj);
  },
  removePrice({ commit, dispatch, getters }, priceId) {
    commit('REMOVE_PRICE_FROM_PRODUCT', priceId);
  },
  addCoupon({ commit, dispatch, getters }, couponObj) {
    commit('ADD_COUPON', couponObj);
  },
  removeCoupon({ commit, dispatch, getters }, couponId) {
    commit('REMOVE_COUPON', couponId);
  },
  // validate({ commit, dispatch, state }) {
  //   dispatch('validateFitness');
  //   return dispatch('validateNutrition');
  // },

  // Validates the current user's tokens and refreshes them
  // with new data from the API.
  validate({ commit, state }) {
    if (!state.currentUser) {
      return Promise.resolve(null);
    }
    if (state.authChecked && state.currentUser) {
      return Promise.resolve(state.currentUser);
    }

    return axios
      .post(api.URL_VALIDATE_AUTH_TOKEN)
      .then((response) => {
        const user = response.data;

        commit('SET_CURRENT_USER_FITNESS_TOKEN', user);
        commit('SET_ACTIVE_PRODUCTS', user);
        commit('SET_AUTH_CHECKED', true);
        return user;
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          commit('SET_CURRENT_USER', null);
        } else {
          console.warn(error);
        }
        return null;
      });
  },
};

// ===
// Private helpers
// ===
function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key));
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state));
}

function setDefaultAuthHeaders(state) {
  // fitness token is headers.token
  if (state.currentUser && state.currentUser.roles) {
    if (state.currentUser.roles.isAdmin) {
      axios.defaults.headers.common.token = state.currentUser.adminToken;
    } else if (state.currentUser.roles.isTrainer) {
      axios.defaults.headers.common.token = state.currentUser.trainerToken;
    }
  }
}

// Stream unread count event handler
function countUnread(event) {
  if (event.total_unread_count !== undefined) {
    store.commit('auth/SET_UNREAD_COUNT', event.total_unread_count);
  }
}
